import { Box, Button, SxProps, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useState } from 'react';
import { FileRejection } from 'react-dropzone';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { ReactComponent as RemoveIcon } from '../../../../assets/images/icon-delete-trashbin.svg';

import { IMAGE_TYPES, MAX_IMAGE_SIZE_BYTES } from '../../../../constants/upload';
import {
  BigIconWrapper,
  ButtonWrapper,
  RemoveImageButton,
  StyledEmptyUploaderContent,
  StyledImage,
  StyledUploadedImageContainer,
} from './FileUploader.styles';
import { FileUploaderBase } from './FileUploaderBase';

interface IProps {
  title?: string;
  helperText?: string;
  accept?: string | string[];
  maxSize?: number;
  onDrop?: (acceptedFiles: File[], fileRejections: FileRejection[]) => void;
  onDropRejected?: (fileRejections: FileRejection[]) => void;
  fileUrl?: string;
  fileSx?: SxProps<Theme>;
  onRemove?: () => void;
  onResetToDefault?: () => void;
  showRemove?: boolean;
  height?: number | string;
  dataAction?: string;
  changeButtonTooltip?: string;
  isInherited?: boolean;
  placeholderFooter?: React.ReactNode;
}

export const ImageUploader: React.FC<IProps> = ({
  title,
  helperText,
  accept = IMAGE_TYPES,
  maxSize = MAX_IMAGE_SIZE_BYTES,
  onDrop,
  onDropRejected,
  fileUrl,
  fileSx,
  onRemove,
  onResetToDefault,
  showRemove = true,
  height = '100%',
  dataAction = '',
  changeButtonTooltip,
  isInherited = false,
  placeholderFooter,
}) => {
  const theme = useTheme();

  const [isHovered, setIsHovered] = useState(false);

  const uploadedImage = useCallback(
    (open: () => void) => {
      if (!fileUrl) {
        return null;
      }
      return (
        <StyledUploadedImageContainer
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <StyledImage src={fileUrl} alt="Uploaded image" sx={fileSx} />
          {isHovered && (
            <>
              <ButtonWrapper>
                <Tooltip title={changeButtonTooltip || ''} arrow placement="top">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      e.preventDefault();
                      open();
                    }}
                    data-action={`${dataAction}_change_image_btn`}
                  >
                    Change image
                  </Button>
                </Tooltip>
                {showRemove && !isInherited && (
                  <RemoveImageButton
                    onClick={onRemove}
                    aria-label="Remove image"
                    data-action={`${dataAction}_remove_image_btn`}
                  >
                    <RemoveIcon />
                  </RemoveImageButton>
                )}
                {onResetToDefault && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onResetToDefault();
                    }}
                    data-action={`${dataAction}_reset_to_default_btn`}
                  >
                    Reset to default
                  </Button>
                )}
              </ButtonWrapper>
              {isInherited && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '23px',
                    backgroundColor: alpha(theme.palette.common.white, 0.8),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Inherited (not removable)
                  </Typography>
                </Box>
              )}
            </>
          )}
        </StyledUploadedImageContainer>
      );
    },
    [fileUrl, fileSx, isHovered, onRemove, onResetToDefault, showRemove, dataAction, changeButtonTooltip, isInherited],
  );

  const fileUploaderBaseStyles: SxProps<Theme> = {
    height,
    ...(fileUrl && {
      border: 0,
      borderStyle: 'none',
    }),
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FileUploaderBase
        accept={accept}
        maxSize={maxSize}
        multiple={false}
        onDrop={onDrop}
        onDropRejected={onDropRejected}
        sx={fileUploaderBaseStyles}
        dataAction={dataAction}
      >
        {({ open }) => (
          <>
            {!fileUrl && (
              <StyledEmptyUploaderContent>
                <BigIconWrapper>
                  <AddPhotoAlternateOutlinedIcon fontSize="large" sx={{ color: theme.palette.action.active }} />
                </BigIconWrapper>
                <Typography variant="body1" fontWeight={theme.typography.fontWeightRegular}>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {helperText}
                </Typography>
                {placeholderFooter && (
                  <Box
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    sx={{ width: '100%', mt: 2 }}
                  >
                    {placeholderFooter}
                  </Box>
                )}
              </StyledEmptyUploaderContent>
            )}
            {uploadedImage(open)}
          </>
        )}
      </FileUploaderBase>
    </Box>
  );
};
